import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { TreeView, TreeItem } from '@mui/x-tree-view';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import { Category } from '../../types/Category';
import { Document } from '../../types/Document';
import { fetchCategoryWithChildren } from '../../services/CategoryService';

export interface ImportSubtreeFromDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  documents: Document[];
  /** Omit from source list (usually the active document). */
  excludeDocumentId?: string;
  /** Current tab name shown for context */
  targetDocumentLabel?: string;
  /** Category that will receive the cloned subtree */
  targetCategoryName: string;
  onRefreshDocuments?: () => void | Promise<void>;
  onConfirm: (sourceSubtreeRoot: Category) => void | Promise<void>;
}

function collectCategoryIds(cat: Category): string[] {
  const ids = [cat.id];
  if (cat.children?.length) {
    cat.children.forEach(ch => ids.push(...collectCategoryIds(ch)));
  }
  return ids;
}

function findCategoryById(root: Category, id: string): Category | null {
  if (root.id === id) return root;
  if (!root.children) return null;
  for (const ch of root.children) {
    const found = findCategoryById(ch, id);
    if (found) return found;
  }
  return null;
}

const ImportSubtreeFromDocumentDialog: React.FC<ImportSubtreeFromDocumentDialogProps> = ({
  open,
  onClose,
  documents,
  excludeDocumentId,
  targetDocumentLabel,
  targetCategoryName,
  onRefreshDocuments,
  onConfirm,
}) => {
  const [selectedDocId, setSelectedDocId] = useState<string>('');
  /** Stable fetch key; avoids re-fetch loops when `documents` churns (e.g. subcase refreshes). */
  const [sourceRootCategoryId, setSourceRootCategoryId] = useState<string | null>(null);
  const [sourceRoot, setSourceRoot] = useState<Category | null>(null);
  const [loadingTree, setLoadingTree] = useState(false);
  const [treeError, setTreeError] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const docsWithCategory = useMemo(
    () =>
      [...documents]
        .filter(
          d =>
            d.categoryId &&
            (!excludeDocumentId || d.id !== excludeDocumentId),
        )
        .sort((a, b) => (a.label || a.name).localeCompare(b.label || b.name)),
    [documents, excludeDocumentId],
  );

  useEffect(() => {
    if (!open) return;
    void onRefreshDocuments?.();
    // Intentionally omit onRefreshDocuments from deps — identity changes would re-fetch the doc list endlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSelectedDocId('');
      setSourceRootCategoryId(null);
      setSourceRoot(null);
      setLoadingTree(false);
      setTreeError(null);
      setSelectedNodeId(null);
      setSubmitting(false);
      return;
    }
    setSelectedDocId('');
    setSourceRootCategoryId(null);
    setSourceRoot(null);
    setTreeError(null);
    setSelectedNodeId(null);
  }, [open]);

  useEffect(() => {
    if (!open || !sourceRootCategoryId) {
      setSourceRoot(null);
      setTreeError(null);
      setSelectedNodeId(null);
      setLoadingTree(false);
      return;
    }

    let cancelled = false;
    setLoadingTree(true);
    setTreeError(null);
    setSourceRoot(null);
    setSelectedNodeId(null);

    fetchCategoryWithChildren(sourceRootCategoryId)
      .then(tree => {
        if (!cancelled) {
          setSourceRoot(tree);
          setSelectedNodeId(tree.id);
        }
      })
      .catch(e => {
        if (!cancelled) {
          console.error(e);
          setTreeError(
            e instanceof Error ? e.message : 'Failed to load category tree',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingTree(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, sourceRootCategoryId]);

  const expandedIds = useMemo(
    () => (sourceRoot ? collectCategoryIds(sourceRoot) : []),
    [sourceRoot],
  );

  const renderTreeItems = (category: Category): React.ReactNode => {
    const hasChildren = Boolean(category.children?.length);
    return (
      <TreeItem
        key={category.id}
        nodeId={category.id}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25 }}>
            {category.contentCategory ? (
              <DescriptionIcon fontSize="small" color="action" />
            ) : (
              <FolderIcon fontSize="small" color="action" />
            )}
            <Typography variant="body2">
              {category.name}
              {category.contentCategory && category.contents?.length ? (
                <Typography component="span" variant="caption" color="text.secondary">
                  {` (${category.contents.length} items)`}
                </Typography>
              ) : null}
            </Typography>
          </Box>
        }
      >
        {hasChildren
          ? category.children!.map(child => renderTreeItems(child))
          : null}
      </TreeItem>
    );
  };

  const handleConfirm = async () => {
    if (!sourceRoot || !selectedNodeId) return;
    const picked = findCategoryById(sourceRoot, selectedNodeId);
    if (!picked) {
      alert('Could not resolve selected category.');
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm(picked);
      onClose();
    } catch (e) {
      console.error(e);
      alert(
        `Import failed: ${e instanceof Error ? e.message : 'Unknown error'}`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import subtree from document</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Clone will be placed under{' '}
          <strong>{targetCategoryName || '(no category selected)'}</strong>
          {targetDocumentLabel ? (
            <>
              {' '}
              in <strong>{targetDocumentLabel}</strong>
            </>
          ) : null}
          .
        </Typography>

        {docsWithCategory.length === 0 ? (
          <Alert severity="info">No documents available.</Alert>
        ) : (
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel id="import-subtree-source-doc">Source document</InputLabel>
            <Select
              labelId="import-subtree-source-doc"
              label="Source document"
              value={selectedDocId}
              onChange={e => {
                const id = e.target.value as string;
                setSelectedDocId(id);
                const doc = docsWithCategory.find(d => d.id === id);
                setSourceRootCategoryId(doc?.categoryId ?? null);
              }}
            >
              {docsWithCategory.map(doc => (
                <MenuItem key={doc.id} value={doc.id}>
                  {doc.label || doc.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {loadingTree && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {treeError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {treeError}
          </Alert>
        )}

        {!loadingTree && sourceRoot && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Category to copy (including descendants)
            </Typography>
            <Box
              sx={{
                maxHeight: 360,
                overflow: 'auto',
                border: theme => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 1,
              }}
            >
              <TreeView
                key={sourceRoot.id}
                defaultCollapseIcon={<ExpandMoreIcon />}
                defaultExpandIcon={<ChevronRightIcon />}
                defaultExpanded={expandedIds}
                selected={selectedNodeId || ''}
                onNodeSelect={(_e, id) => setSelectedNodeId(id)}
              >
                {renderTreeItems(sourceRoot)}
              </TreeView>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleConfirm()}
          disabled={
            submitting ||
            !sourceRoot ||
            !selectedNodeId ||
            loadingTree ||
            docsWithCategory.length === 0
          }
        >
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportSubtreeFromDocumentDialog;
